import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getVideosByCategory, getHotVideos } from '@/mocks/videos';
import VideoCard from '@/components/base/VideoCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || 'recommended');
  const videos = useMemo(() => getVideosByCategory(slug || 'recommended'), [slug]);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: category.name },
  ];

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 西瓜视频`,
    description: category.description,
    url: `https://www.xigua-video.com/category/${category.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '西瓜视频',
      url: 'https://www.xigua-video.com/',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }}
      />
      <main role="main">
        <Breadcrumb items={breadcrumbItems} />

        <section className="px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
                  <i className={`${category.icon} text-primary-600 text-lg`}></i>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {category.name}
                </h1>
              </div>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
                {category.description}
              </p>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    cat.id === category.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {videos.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {videos.map((video) => (
                  <VideoCard
                    key={video.id}
                    id={video.id}
                    title={video.title}
                    thumbnail={video.thumbnail}
                    duration={video.duration}
                    views={video.views}
                    rating={video.rating}
                    tags={video.tags}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-video-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无该分类的视频内容</p>
                <Link
                  to="/category/recommended"
                  className="inline-flex items-center gap-1 mt-4 text-sm text-primary-600 font-medium cursor-pointer"
                >
                  去看看热门推荐
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            )}
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
              你可能也喜欢
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {getHotVideos().slice(0, 4).map((video) => (
                <VideoCard
                  key={video.id}
                  id={video.id}
                  title={video.title}
                  thumbnail={video.thumbnail}
                  duration={video.duration}
                  views={video.views}
                  rating={video.rating}
                  tags={video.tags}
                />
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}